import { PlusCircleOutlined, SearchOutlined, GlobalOutlined } from "@ant-design/icons";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { 
  IconButton, 
  Box, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../axios/axios-instance";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import LabeledInput from "../../../components/common/fields/LabeledInput";
import SearchInput from "../../../components/common/fields/SearchInput";
import SelectField from "../../../components/common/fields/SelectField";
import StatusBadge, { StatusBadgeTone } from "../../../components/common/StatusBadge";
import EndpointTranslationModal from "../../../components/common/EndpointTranslationModal";
import Card from "../../../components/common/Card";
import MuiTable from "../../../components/common/MuiTable";
import { Column } from "../../../utils/constants";
import { pathAPI } from "../../../utils/pathApi";

interface MessageCode {
  id: number;
  code: string;
  message: string;
  http_code: number;
  description: string;
}

interface PaginatedResponse {
  data: MessageCode[];
  current_page: number;
  from: number;
  to: number;
  last_page: number;
  per_page: number;
  total_page: number;
  total: number;
}

interface MessageCodeFormData {
  code: string;
  message: string;
  http_code: number;
  description: string;
}

const httpStatusCodes = [
  { value: 200, label: "200 OK" },
  { value: 201, label: "201 Created" },
  { value: 204, label: "204 No Content" },
  { value: 400, label: "400 Bad Request" },
  { value: 401, label: "401 Unauthorized" },
  { value: 403, label: "403 Forbidden" },
  { value: 404, label: "404 Not Found" },
  { value: 405, label: "405 Method Not Allowed" },
  { value: 409, label: "409 Conflict" },
  { value: 422, label: "422 Unprocessable Entity" },
  { value: 429, label: "429 Too Many Requests" },
  { value: 500, label: "500 Internal Server Error" },
  { value: 502, label: "502 Bad Gateway" },
  { value: 503, label: "503 Service Unavailable" },
  { value: 504, label: "504 Gateway Timeout" },
];

const DEFAULT_PAGE_SIZE = 20;

const MessageCodes = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MessageCode[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  
  // Pagination states (server-side, page is 0-based)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  
  // Modal states
  const [openDialog, setOpenDialog] = useState(false);
  const [currentMessage, setCurrentMessage] = useState<MessageCode | null>(null);
  const [formData, setFormData] = useState<MessageCodeFormData>({
    code: "",
    message: "",
    http_code: 400,
    description: ""
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  
  // Translation states
  const [translationModalVisible, setTranslationModalVisible] = useState(false);
  const [selectedMessageCode, setSelectedMessageCode] = useState<MessageCode | undefined>(undefined);

  // Notification states
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning"
  });

  const fetchMessageCodes = async (
    keyword: string = searchKeyword,
    nextPage: number = page,
    nextSize: number = rowsPerPage
  ) => {
    try {
      setLoading(true);

      const response = await axiosInstance.get<PaginatedResponse>(pathAPI.errorMessages, {
        params: {
          keyword: keyword.trim() || undefined,
          page: nextPage,
          size: nextSize,
        },
      });
      
      if (response.data) {
        // Map the data correctly to include action property for each item
        const updatedData = Array.isArray(response.data.data) 
          ? response.data.data.map((item) => ({
              ...item,
              action: item.id
            }))
          : [];
        
        console.log("Fetched message codes:", updatedData);
        
        // Update state with the fetched data
        setData(updatedData);
        setTotalItems(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching message codes:", error);
      showAlert(t("message_code.fetch_error"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessageCodes();
  }, []);

  useEffect(() => {
    if (currentMessage) {
      setFormData({
        code: currentMessage.code,
        message: currentMessage.message,
        http_code: currentMessage.http_code || 400,
        description: currentMessage.description || ""
      });
    } else {
      setFormData({
        code: "",
        message: "",
        http_code: 400,
        description: ""
      });
    }
  }, [currentMessage]);

  const handleSearch = () => {
    setPage(0);
    fetchMessageCodes(searchKeyword, 0, rowsPerPage);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchMessageCodes(searchKeyword, nextPage, rowsPerPage);
  };

  const handleRowsPerPageChange = (nextSize: number) => {
    setRowsPerPage(nextSize);
    setPage(0);
    fetchMessageCodes(searchKeyword, 0, nextSize);
  };

  const handleAdd = () => {
    setCurrentMessage(null);
    setOpenDialog(true);
  };

  const handleEdit = (messageCode: MessageCode) => {
    setCurrentMessage(messageCode);
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    try {
      setDeleting(id);
      await axiosInstance.delete(`${pathAPI.errorMessages}/${id}`);
      showAlert(t("message_code.delete_ok"), "success");
      fetchMessageCodes(searchKeyword);
    } catch (error) {
      console.error("Error deleting message code:", error);
      showAlert(t("message_code.delete_error"), "error");
    } finally {
      setDeleting(null);
    }
  };

  const handleFormChange = (field: keyof MessageCodeFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFormSubmit = async () => {
    if (!formData.code.trim() || !formData.message.trim()) {
      showAlert(t("message_code.required"), "error");
      return;
    }

    try {
      setFormSubmitting(true);
      
      // Prepare payload with required fields
      const payload: {
        code: string;
        message: string;
        description?: string;
        http_code?: number;
      } = {
        code: formData.code.trim(),
        message: formData.message.trim(),
      };

      // Add optional fields only if they have values
      if (formData.description && formData.description.trim()) {
        payload.description = formData.description.trim();
      }
      
      if (formData.http_code) {
        payload.http_code = formData.http_code;
      }

      if (currentMessage) {
        // Update existing message code
        await axiosInstance.put(`${pathAPI.errorMessages}/${currentMessage.id}`, payload);
        showAlert(t("message_code.updated_ok"), "success");
      } else {
        // Create new message code
        await axiosInstance.post(pathAPI.errorMessages, payload);
        showAlert(t("message_code.created_ok"), "success");
      }

      setOpenDialog(false);
      fetchMessageCodes(searchKeyword);
    } catch (error) {
      console.error("Error saving message code:", error);
      showAlert(t("message_code.save_error"), "error");
    } finally {
      setFormSubmitting(false);
    }
  };

  const showAlert = (message: string, severity: "success" | "error" | "info" | "warning") => {
    setAlert({
      open: true,
      message,
      severity
    });
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({
      ...prev,
      open: false
    }));
  };

  const getStatusTone = (status: number): StatusBadgeTone => {
    if (status >= 200 && status < 300) return "success";
    if (status >= 300 && status < 400) return "info";
    if (status >= 400 && status < 500) return "warning";
    if (status >= 500) return "error";
    return "default";
  };

  const header: Column[] = [
    {
      id: "id",
      label: t("message_code.col.id"),
      minWidth: 50,
      align: "center",
    },
    {
      id: "code",
      label: t("message_code.col.code"),
      minWidth: 120,
      align: "center",
    },
    {
      id: "message",
      label: t("message_code.col.message"),
      minWidth: 200,
      align: "center",
    },
    {
      id: "http_code",
      label: t("message_code.col.http"),
      minWidth: 120,
      align: "center",
      format: (value) => (
        <StatusBadge label={String(value || t("message_code.na"))} tone={getStatusTone(value)} />
      ),
    },
    {
      id: "description",
      label: t("message_code.col.description"),
      minWidth: 200,
      align: "center",
      format: (value) => value || t("message_code.na"),
    },
    {
      id: "action",
      label: t("message_code.col.actions"),
      minWidth: 200,
      align: "center",
      format: (value: any) => {
        const messageCode = data.find(item => item.id === value);
        const isDeleting = deleting === value;
        
        return (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={2}
          >
            <IconButton
              size={"small"}
              onClick={() => messageCode && handleManageTranslations(messageCode)}
              title={t("message_code.translations")}
            >
              <GlobalOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
            </IconButton>
            <IconButton
              size={"small"}
              onClick={() => messageCode && handleEdit(messageCode)}
            >
              <EditIcon color="success" />
            </IconButton>
            <IconButton
              size={"small"}
              onClick={() => handleDelete(value)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <CircularProgress size={20} />
              ) : (
                <DeleteIcon color={"error"} />
              )}
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleManageTranslations = (messageCode: MessageCode) => {
    setSelectedMessageCode(messageCode);
    setTranslationModalVisible(true);
  };

  return (
    <Card>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box width={300}>
            <SearchInput
              placeholder={t("message_code.search_ph")}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onSearch={handleSearch}
            />
          </Box>
          <ActionButton appSize="large" startIcon={<SearchOutlined />} onClick={handleSearch} disabled={loading}>
            {t("message_code.search")}
          </ActionButton>
        </Box>
        <ActionButton appSize="large" startIcon={<PlusCircleOutlined />} onClick={handleAdd}>
          {t("message_code.add_new")}
        </ActionButton>
      </Box>
      
      <MuiTable
        header={header}
        data={data}
        isLoading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalElements={totalItems}
        onPageChange={(_, nextPage) => handlePageChange(nextPage)}
        onRowsPerPageChange={(event) => handleRowsPerPageChange(Number(event.target.value))}
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => !formSubmitting && setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentMessage ? t("message_code.dialog.edit_title") : t("message_code.dialog.add_title")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <LabeledInput
              label={t("message_code.dialog.code")}
              required
              value={formData.code}
              onChange={(e) => handleFormChange("code", e.target.value)}
              placeholder="ERR_INVALID_INPUT"
            />

            <LabeledInput
              label={t("message_code.dialog.message")}
              required
              multiline
              minRows={2}
              value={formData.message}
              onChange={(e) => handleFormChange("message", e.target.value)}
              placeholder={t("message_code.dialog.message_ph")}
            />

            <Box>
              <Box component="label" sx={{ display: "block", mb: 0.5, fontSize: "14px" }}>
                {t("message_code.dialog.http")}
              </Box>
              <SelectField
                value={formData.http_code}
                searchable={false}
                matchAnchorWidth
                options={httpStatusCodes}
                onChange={(value) => handleFormChange("http_code", Number(value))}
              />
            </Box>

            <LabeledInput
              label={t("message_code.dialog.description")}
              multiline
              minRows={3}
              value={formData.description}
              onChange={(e) => handleFormChange("description", e.target.value)}
              placeholder={t("message_code.dialog.description_ph")}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CancelButton onClick={() => setOpenDialog(false)} disabled={formSubmitting}>
            {t("btn.cancel")}
          </CancelButton>
          <ActionButton onClick={handleFormSubmit} disabled={formSubmitting}>
            {formSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t("message_code.dialog.saving")}
              </>
            ) : (
              t("btn.save")
            )}
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Alert snackbar */}
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Translation Modal - For handling message code translations */}
      <EndpointTranslationModal
        open={translationModalVisible}
        onClose={() => setTranslationModalVisible(false)}
        endpoint={`${pathAPI.errorMessages}/${selectedMessageCode?.id}/translations`}
        title={t("common.translation_modal.manage_title")}
        subtitle={selectedMessageCode?.code}
        fallbackSourceText={selectedMessageCode?.message}
      />
    </Card>
  );
};

export default MessageCodes;